import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../../contexts/UserContext'
import { StakeholderType } from '../../types/user'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'

interface AcceptModalProps {
  isOpen: boolean
  onClose: () => void
  autoRedirect?: boolean
}

export default function AcceptModal({
  isOpen,
  onClose,
  autoRedirect = true,
}: AcceptModalProps) {
  const navigate = useNavigate()
  const { user } = useUser()
  const [countdown, setCountdown] = useState(3)

  const getRedirectPath = useCallback(() => {
    // Always redirect to /projects - ProjectsRoute will handle driver redirects to /tags
    return '/projects'
  }, [])

  const handleRedirect = useCallback(() => {
    navigate(getRedirectPath())
  }, [navigate, getRedirectPath])

  const handleExit = useCallback(() => {
    navigate('/login')
  }, [navigate])

  useEffect(() => {
    if (!isOpen || !autoRedirect) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          handleRedirect()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isOpen, autoRedirect, handleRedirect])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
            Welcome to the dedicated <br /> Dump Truck Ecosystem! 🎉
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            Congratulations, you are now a Registered User of the Ecosystem!
            Click the button below to access your dashboard or press Exit and
            you will be redirected to our homepage.
          </p>
          <div className="pt-4 space-y-3">
            {autoRedirect ? (
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Redirecting to dashboard in {countdown} seconds...
                </p>
              </div>
            ) : (
              <div className="text-center" />
            )}
            <Button
              type="button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleRedirect}
            >
              Continue to Dashboard
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
              onClick={handleExit}
            >
              Exit
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
